/*
  Rising mark — a large masked isotipo that rises and fades in with the
  scroll and tilts toward the pointer (touch: leans with scroll velocity).
  Used by the landing's mark-portal section (.mark-portal) and by any
  section that opts in with [data-mark-rise] (e.g. "Xeridas hoy"). Each
  instance reads its settings from data attributes on the section and
  moves its .mark-portal__mark child.
*/
(function () {
  function initMarkRise(section) {
    var mark = section.querySelector('.mark-portal__mark');
    if (!mark || section.dataset.markReady) return;
    section.dataset.markReady = 'true';

    // ── Read settings from data attributes ──────────────────────────────────
    var REST_OPACITY   = (parseFloat(section.dataset.markOpacity)   || 15)  / 100;
    var START_Y        =  parseFloat(section.dataset.riseDistance)  || 80;
    var TILT_SCALE     =  parseFloat(section.dataset.tiltIntensity) || 5;
    var MAX_TILT       = TILT_SCALE * 0.6; // 0–6 deg across the 0–10 range

    // ── Reduced motion: static rest state, no listeners ─────────────────────
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      mark.style.opacity   = REST_OPACITY;
      mark.style.transform = 'none';
      return;
    }

    // ── State ────────────────────────────────────────────────────────────────
    var currentY       = START_Y;
    var currentOpacity = 0;
    var currentTiltX   = 0;
    var currentTiltY   = 0;

    var targetY        = START_Y;
    var targetOpacity  = 0;
    var targetTiltX    = 0;
    var targetTiltY    = 0;

    // Normalized pointer position (0–1) within section bounds
    var pointerNormX   = 0.5;
    var pointerNormY   = 0.5;

    var lastScrollY    = window.scrollY;
    var scrollVelocity = 0;

    var isVisible      = false;
    var rafId          = null;

    // Fine pointer = mouse; coarse = touch. Drives which tilt path runs.
    var hasFinePointer = window.matchMedia('(pointer: fine)').matches;

    // Lerp factors — lower = slower/smoother
    var RISE_LERP = 0.07;
    var TILT_LERP = 0.10;

    // ── Scroll target computation ────────────────────────────────────────────
    function updateScrollTargets() {
      var rect = section.getBoundingClientRect();
      var vh   = window.innerHeight;

      // 0 when section bottom first enters viewport bottom;
      // 1 when section top exits viewport top.
      var progress = (vh - rect.top) / (vh + rect.height);
      progress = Math.max(0, Math.min(1, progress));

      // Rise: START_Y down → 0 at mid-scroll → slight overshoot upward
      if (progress <= 0.5) {
        targetY = START_Y * (1 - progress * 2);
      } else {
        targetY = -START_Y * 0.25 * ((progress - 0.5) * 2);
      }

      // Opacity: fade from 0 to REST_OPACITY across first 40% of scroll progress
      var opacityProgress = (progress - 0.05) / 0.40;
      opacityProgress = Math.max(0, Math.min(1, opacityProgress));
      targetOpacity = opacityProgress * REST_OPACITY;

      // Track scroll velocity — used for touch tilt
      var sy     = window.scrollY;
      scrollVelocity = sy - lastScrollY;
      lastScrollY    = sy;
    }

    // ── Tilt target computation ──────────────────────────────────────────────
    function updateTiltTargets() {
      if (MAX_TILT === 0) return;

      if (hasFinePointer) {
        // Desktop: cursor position within section
        targetTiltY = (pointerNormX - 0.5) * MAX_TILT * 2;   // left→right rotation
        targetTiltX = (pointerNormY - 0.5) * MAX_TILT * -2;  // up→down rotation (inverted for natural feel)
      } else {
        // Touch: scroll velocity drives a gentle forward/backward lean
        targetTiltX = Math.max(-MAX_TILT, Math.min(MAX_TILT, scrollVelocity * -0.25));
        targetTiltY = 0;
      }
    }

    // ── Single rAF tick — one transform write, both effects composed ─────────
    function tick() {
      rafId = null;

      updateScrollTargets();
      updateTiltTargets();

      // Lerp all values toward their targets
      currentY       += (targetY       - currentY)       * RISE_LERP;
      currentOpacity += (targetOpacity - currentOpacity) * RISE_LERP;
      currentTiltX   += (targetTiltX   - currentTiltX)   * TILT_LERP;
      currentTiltY   += (targetTiltY   - currentTiltY)   * TILT_LERP;

      // perspective() in the transform chain — avoids overflow:hidden +
      // parent-perspective compositor conflicts in Chrome/Safari.
      mark.style.transform =
        'perspective(1000px)' +
        ' translateY('  + currentY.toFixed(2)     + 'px)' +
        ' rotateX('     + currentTiltX.toFixed(3) + 'deg)' +
        ' rotateY('     + currentTiltY.toFixed(3) + 'deg)';

      mark.style.opacity = currentOpacity.toFixed(4);

      // Sleep once everything has settled; the next scroll or pointer move
      // wakes it. Running every frame while merely visible kept the main
      // thread busy for nothing (battery, INP).
      var settled =
        Math.abs(targetY       - currentY)       < 0.05 &&
        Math.abs(targetOpacity - currentOpacity) < 0.0005 &&
        Math.abs(targetTiltX   - currentTiltX)   < 0.005 &&
        Math.abs(targetTiltY   - currentTiltY)   < 0.005;

      if (isVisible && !settled) {
        rafId = requestAnimationFrame(tick);
      }
    }

    function schedule() {
      if (!rafId && isVisible) {
        rafId = requestAnimationFrame(tick);
      }
    }

    // ── Event handlers ───────────────────────────────────────────────────────
    function onScroll() { schedule(); }

    function onPointerMove(e) {
      var rect    = section.getBoundingClientRect();
      pointerNormX = (e.clientX - rect.left) / rect.width;
      pointerNormY = (e.clientY - rect.top)  / rect.height;
      schedule();
    }

    function onPointerLeave() {
      // Ease back to center when cursor exits section
      pointerNormX = 0.5;
      pointerNormY = 0.5;
      schedule();
    }

    // ── Cleanup ──────────────────────────────────────────────────────────────
    // Only when the section leaves the DOM (theme editor). Not on pagehide:
    // that also fires when the page enters the back/forward cache, and the
    // restored page would come back with a frozen, possibly invisible mark.
    function cleanup() {
      window.removeEventListener('scroll',   onScroll);
      if (hasFinePointer && MAX_TILT > 0) {
        section.removeEventListener('pointermove',  onPointerMove);
        section.removeEventListener('pointerleave', onPointerLeave);
      }
      io.disconnect();
      mo.disconnect();
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    }

    // ── Attach listeners ─────────────────────────────────────────────────────
    window.addEventListener('scroll', onScroll, { passive: true });

    if (hasFinePointer && MAX_TILT > 0) {
      section.addEventListener('pointermove',  onPointerMove);
      section.addEventListener('pointerleave', onPointerLeave);
    }

    // ── IntersectionObserver — gate rAF to viewport proximity ───────────────
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          isVisible = entry.isIntersecting;
          if (isVisible) {
            schedule();
          } else if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
        });
      },
      { rootMargin: '100px 0px 100px 0px', threshold: 0 }
    );

    io.observe(section);

    // ── MutationObserver — clean up if section is removed from DOM ───────────
    var mo = new MutationObserver(function () {
      if (!document.contains(section)) cleanup();
    });

    // Observe the direct parent only (no subtree needed — section removal
    // changes the parent's childList without requiring subtree traversal).
    if (section.parentNode) {
      mo.observe(section.parentNode, { childList: true });
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.mark-portal, [data-mark-rise]').forEach(initMarkRise);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: a re-rendered section brings a fresh, uninitialised mark.
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
})();
